import { Button } from 'primereact/button';
import { useEffect, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ErrorMessage from '../../../../../../../../../../../../../components/error-message';
import {
  CalculateTransportEmissionResponse,
  useCalculateTransportEmissions,
  WeightUnit,
} from '../../../../../../../../../../../../../hooks/api/transportation';
import useNotificationContext from '../../../../../../../../../../../../../hooks/notification/useNotificationContext';
import { CalculateRouteErrorCode } from '../../../../../../../../../../../../../shared/enums/calculate-route-transport-process-error-code';
import { ToastSeverity } from '../../../../../../../../../../../../../shared/enums/toast-severity';
import {
  IProcessDetails,
  MainRoute,
} from '../../../../../../../../../../../../../shared/interfaces/IFootprint';

type CalculateRouteButtonProps = {
  processData: IProcessDetails;
  processIndex: number;
};

const CalculateRouteButton = ({ processData, processIndex }: CalculateRouteButtonProps) => {
  const { t } = useTranslation();
  const { notify } = useNotificationContext();

  const { control, trigger, setValue } = useFormContext();

  const {
    field: { value: calculatedRouteValue, onChange, ref },
    fieldState: { error },
  } = useController({ name: `processes[${processIndex}].route_calculation`, control });

  const [isClicked, setIsClicked] = useState(false);

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const formErrorMessage =
    (error as any)?.distance?.message ||
    (error as any)?.total_emissions?.message ||
    error?.message ||
    '';
  /* eslint-enable @typescript-eslint/no-explicit-any */

  const onError = (error: Error) => {
    if (error.message === CalculateRouteErrorCode.API_CALCULATION_ERROR) {
      notify({
        severity: ToastSeverity.ERROR,
        detail: t(
          'footprintDetailsPage.sections.calculatePcfSection.transportProcessSection.toastMessages.error.failedToCalculate',
        ),
        life: 5000,
      });
    } else {
      notify({
        severity: ToastSeverity.ERROR,
        detail: error.message,
        life: 5000,
      });
    }
  };

  const onSuccess = (data: CalculateTransportEmissionResponse) => {
    // Update the form value with the calculated data
    /* eslint-disable camelcase */
    onChange(
      {
        distance: data.distance,
        total_emissions: data.totalEmissions,
      },
      { shouldValidate: true },
    );
    /* eslint-enable camelcase */
  };

  const { mutate: calculateTransportEmission, isLoading: isCalculateLoading } =
    useCalculateTransportEmissions({
      onSuccess,
      onError,
    });

  const validateTransportationCalculateForm = async () => {
    return await trigger([
      `processes.${processIndex}.route_name`,
      `processes.${processIndex}.end_location`,
      `processes.${processIndex}.starting_point`,
      `processes.${processIndex}.main_route`,
      `processes.${processIndex}.product_weight`,
    ]);
  };

  const onCalculateRouteClick = async () => {
    setIsClicked(true);

    // Manually validate the transport process form fields
    const isTransportFormValid = await validateTransportationCalculateForm();

    // If the form is valid, proceed to calculate the route and call the API
    if (isTransportFormValid) {
      calculateTransportEmission({
        endLocation: processData?.end_location?.coordinates,
        startLocation: processData?.starting_point?.coordinates,
        transportMode: processData?.main_route as MainRoute,
        unit: WeightUnit.KILOGRAMS,
        weight: processData?.product_weight as number,
      });
    }
  };

  const onResetClick = () => {
    setIsClicked(false);

    // Reset respective transport process form values
    /* eslint-disable camelcase */
    setValue(`processes[${processIndex}]`, {
      ...processData,
      route_name: '',
      route_calculation: null,
      product_weight: null,
      main_route: null,
      starting_point: null,
      end_location: null,
    });
  };

  // Listen for process detail changes, so that we can validate the form again after clicking calculate route button
  // Special case for React Hook Form, manual triggering disables re-validation. Thus this is necessary
  useEffect(() => {
    if (isClicked) {
      validateTransportationCalculateForm();
    }
  }, [isClicked, processData]);

  return (
    <div className='flex flex-column w-full gap-2'>
      <div className='flex flex-row w-full gap-2'>
        {/* Calculate Route button */}
        <Button
          label={t(
            'footprintDetailsPage.sections.calculatePcfSection.transportProcessSection.buttons.calculateRoute',
          )}
          loading={isCalculateLoading}
          onClick={onCalculateRouteClick}
        />

        {/* Reset button */}
        {calculatedRouteValue && (
          <Button
            label={t(
              'footprintDetailsPage.sections.calculatePcfSection.transportProcessSection.buttons.reset',
            )}
            className='p-button-text p-button-warning'
            onClick={onResetClick}
          />
        )}
      </div>

      {/* Hidden input to enable focus & auto-scroll on error */}
      <div>
        <input readOnly className='opacity-0 p-0 m-0 w-0 h-0 absolute' ref={ref} />
      </div>

      {error && <ErrorMessage message={formErrorMessage} />}
    </div>
  );
};

export default CalculateRouteButton;
